import {
  OutreachTriggerTypes,
  SupportOutreachTriggerFormFields,
} from '../../../../../types';

export type TriggerTypeLabels = {
  issueTriggeredWhenLabel: string;
  issueTriggeredWhenSuffixLabel: string;
  issueClearedWhenLabel: string;
  issueClearedWhenSuffixLabel: string;
};

export type GenericTriggerTypes =
  | OutreachTriggerTypes.MaxPercentage
  | OutreachTriggerTypes.MaxIncreaseRate
  | OutreachTriggerTypes.MaxValue
  | OutreachTriggerTypes.CSPOutageDetected;

const genericTriggerTypes: GenericTriggerTypes[] = [
  OutreachTriggerTypes.MaxPercentage,
  OutreachTriggerTypes.MaxIncreaseRate,
  OutreachTriggerTypes.MaxValue,
  OutreachTriggerTypes.CSPOutageDetected,
];

const getDefaultLabels = (): TriggerTypeLabels => ({
  issueTriggeredWhenSuffixLabel: '',
  issueClearedWhenLabel: '',
  issueClearedWhenSuffixLabel: '',
  issueTriggeredWhenLabel: '',
});

const mapLabelsToTriggerType: Record<GenericTriggerTypes, TriggerTypeLabels> = {
  [OutreachTriggerTypes.MaxPercentage]: {
    issueTriggeredWhenLabel:
      'outreach.form.supportOutreachTriggerSection.maxPercentage.issueTriggeredWhen',
    issueTriggeredWhenSuffixLabel:
      'outreach.form.supportOutreachTriggerSection.maxPercentage.issueTriggeredWhenSuffix',
    issueClearedWhenLabel:
      'outreach.form.supportOutreachTriggerSection.maxPercentage.issueClearedWhen',
    issueClearedWhenSuffixLabel:
      'outreach.form.supportOutreachTriggerSection.maxPercentage.issueClearedWhenSuffix',
  },
  [OutreachTriggerTypes.MaxIncreaseRate]: {
    issueTriggeredWhenLabel:
      'outreach.form.supportOutreachTriggerSection.maxIncreaseRate.issueTriggeredWhen',
    issueTriggeredWhenSuffixLabel:
      'outreach.form.supportOutreachTriggerSection.maxIncreaseRate.issueTriggeredWhenSuffix',
    issueClearedWhenLabel:
      'outreach.form.supportOutreachTriggerSection.maxIncreaseRate.issueClearedWhen',
    issueClearedWhenSuffixLabel:
      'outreach.form.supportOutreachTriggerSection.maxIncreaseRate.issueClearedWhenSuffix',
  },
  [OutreachTriggerTypes.MaxValue]: {
    issueTriggeredWhenLabel:
      'outreach.form.supportOutreachTriggerSection.maxValue.issueTriggeredWhen',
    issueTriggeredWhenSuffixLabel:
      'outreach.form.supportOutreachTriggerSection.maxValue.issueTriggeredWhenSuffix',
    issueClearedWhenLabel:
      'outreach.form.supportOutreachTriggerSection.maxValue.issueClearedWhen',
    issueClearedWhenSuffixLabel:
      'outreach.form.supportOutreachTriggerSection.maxValue.issueClearedWhenSuffix',
  },
  [OutreachTriggerTypes.CSPOutageDetected]: {
    issueTriggeredWhenLabel:
      'outreach.form.supportOutreachTriggerSection.supportCSPOutage.issueTriggeredWhen',
    issueTriggeredWhenSuffixLabel:
      'outreach.form.supportOutreachTriggerSection.supportCSPOutage.issueTriggeredWhenSuffix',
    issueClearedWhenLabel:
      'outreach.form.supportOutreachTriggerSection.supportCSPOutage.issueClearedWhen',
    issueClearedWhenSuffixLabel:
      'outreach.form.supportOutreachTriggerSection.supportCSPOutage.issueClearedWhenSuffix',
  },
};

type SupportGenericTriggerFooterProperty = Record<
  string,
  {
    nameCopyId: string;
    valueCopyId: string;
  }
>;

const maxValueTriggerFooterProperties: SupportGenericTriggerFooterProperty = {
  [SupportOutreachTriggerFormFields.IssueTriggeredWhen]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxValue.issueTriggered',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxValue.issueTriggeredValue',
  },
  [SupportOutreachTriggerFormFields.IssueClearedWhen]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxValue.issueCleared',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxValue.issueClearedValue',
  },
  [SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxPercentage.clearMessage',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxPercentage.clearMessageValue',
  },
};

const maxPercentageTriggerFooterProperties: SupportGenericTriggerFooterProperty = {
  [SupportOutreachTriggerFormFields.IssueTriggeredWhen]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxPercentage.issueTriggered',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxPercentage.issueTriggeredValue',
  },
  [SupportOutreachTriggerFormFields.IssueClearedWhen]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxPercentage.issueCleared',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxPercentage.issueClearedValue',
  },
  [SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxPercentage.clearMessage',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxPercentage.clearMessageValue',
  },
};

const cspOutageDetectedTriggerFooterProperties: SupportGenericTriggerFooterProperty = {
  [SupportOutreachTriggerFormFields.IssueTriggeredWhen]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.cspOutageDetected.issueTriggered',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.cspOutageDetected.issueTriggeredValue',
  },
  [SupportOutreachTriggerFormFields.IssueClearedWhen]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.cspOutageDetected.issueCleared',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.cspOutageDetected.issueClearedValue',
  },
  [SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.cspOutageDetected.clearMessage',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.cspOutageDetected.clearMessageValue',
  },
};

const maxIncreaseRateTriggerFooterProperties: SupportGenericTriggerFooterProperty = {
  [SupportOutreachTriggerFormFields.IssueTriggeredWhen]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxIncreaseRate.issueTriggered',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxIncreaseRate.issueTriggeredValue',
  },
  [SupportOutreachTriggerFormFields.IssueClearedWhen]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxIncreaseRate.issueCleared',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxIncreaseRate.issueClearedValue',
  },
  [SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared]: {
    nameCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxIncreaseRate.clearMessage',
    valueCopyId:
      'outreach.form.supportOutreachTriggerSection.triggerFooter.maxIncreaseRate.clearMessageValue',
  },
};

const mapTriggerFooterPropertiesToType: Record<
  string,
  SupportGenericTriggerFooterProperty
> = {
  [OutreachTriggerTypes.MaxPercentage]: maxPercentageTriggerFooterProperties,
  [OutreachTriggerTypes.MaxValue]: maxValueTriggerFooterProperties,
  [OutreachTriggerTypes.CSPOutageDetected]: cspOutageDetectedTriggerFooterProperties,
  [OutreachTriggerTypes.MaxIncreaseRate]: maxIncreaseRateTriggerFooterProperties,
};

export const getGenericTriggerTypeLabels = (
  triggerType: OutreachTriggerTypes,
): TriggerTypeLabels => {
  const labels =
    mapLabelsToTriggerType[triggerType as GenericTriggerTypes] ||
    getDefaultLabels();
  return labels;
};

export const isGenericTriggerTypeForSupportOutreach = (
  triggerType: OutreachTriggerTypes,
): boolean => {
  return genericTriggerTypes.includes(triggerType as GenericTriggerTypes);
};

export const getTriggerFooterProperty = (
  type: OutreachTriggerTypes,
  field: SupportOutreachTriggerFormFields,
) => {
  return (
    mapTriggerFooterPropertiesToType[type][field] || {
      nameCopyId: '',
      valueCopyId: '',
    }
  );
};
